import { IntlShape } from "react-intl";
import { FieldValues, UseFormGetValues, UseFormSetError } from "react-hook-form";

import type { GetServiceDetailsConfigResponseType } from "api/response";
import { getSelectedInvitedModeValues } from "@RequestFormV2/utils/invitedModesHelpers";
import {
  VALID_DELIVERY_SERVICE_LEVELS,
  isDeliveryServiceLevelFeatureActive,
} from "@RequestFormV2/sections/Schedule/utils/deliveryServiceLevel";
import { setRequiredFieldError } from "@RequestFormV2/validation/utils/setFieldErrors";
import { getRequiredMessage, isEmptyValue } from "@RequestFormV2/validation/utils/validationMessages";

export const INVITED_MODES_ERROR_PATH = "invited_modes";
export const ROAD_FREIGHT_OPTION_ERROR_PATH = "invited_modes.road_freight_option";

export function validateService(
  getValues: UseFormGetValues<FieldValues>,
  setError: UseFormSetError<FieldValues>,
  intl: IntlShape,
  serviceDetailsConfig?: GetServiceDetailsConfigResponseType,
  options: { skipFreightMode?: boolean } = {},
): boolean {
  let isValid = true;
  const serviceOptions = serviceDetailsConfig?.service_options;

  const invitedModes = getValues("invited_modes");

  if (!options.skipFreightMode && getSelectedInvitedModeValues(invitedModes).length === 0) {
    setError(INVITED_MODES_ERROR_PATH, {
      type: "manual",
      message: intl.formatMessage({
        id: "request-form.badge.selectFreightMode",
        defaultMessage: "Select at least one freight mode",
      }),
    });
    isValid = false;
  }

  if (!options.skipFreightMode && invitedModes?.road_freight === true) {
    if (!setRequiredFieldError(setError, ROAD_FREIGHT_OPTION_ERROR_PATH, invitedModes.road_freight_option, intl)) {
      isValid = false;
    }
  }

  const serviceTypeOptions = serviceOptions?.service_type_options;
  if (serviceTypeOptions && serviceTypeOptions.length > 0) {
    if (!setRequiredFieldError(setError, "service.service_type", getValues("service.service_type"), intl)) {
      isValid = false;
    }
  }

  if (serviceOptions?.incoterm === "mandatory") {
    const incoterms = getValues("service.incoterms");
    if (isEmptyValue(incoterms)) {
      setError("service.incoterms", {
        type: "manual",
        message: getRequiredMessage(intl),
      });
      isValid = false;
    }
  }

  if (serviceOptions?.ask_insurance && serviceOptions.ask_insurance !== "off") {
    const insurance = getValues("service.insurance");
    if (insurance !== true && insurance !== false) {
      setError("service.insurance", {
        type: "manual",
        message: intl.formatMessage({
          id: "request-form.service.requirements.validation.required",
          defaultMessage: "This is required",
        }),
      });
      isValid = false;
    }
  }

  const stops = getValues("schedule.stops") as unknown[] | undefined;
  const stopCount = Array.isArray(stops) ? stops.length : 0;
  if (isDeliveryServiceLevelFeatureActive(Boolean(serviceOptions?.request_service_level), stopCount)) {
    const serviceLevel = `${getValues("service.service_level") ?? ""}`;
    if (!(VALID_DELIVERY_SERVICE_LEVELS as readonly string[]).includes(serviceLevel)) {
      setError("service.service_level", {
        type: "manual",
        message: getRequiredMessage(intl),
      });
      isValid = false;
    }
  }

  return isValid;
}

export function isInsuranceUnset(value: unknown): boolean {
  return value !== true && value !== false && isEmptyValue(value);
}
